/**
 * Earlier briefs.
 *
 * COLLAPSED BY DEFAULT, AND THAT IS THE DESIGN. The brief above it is about
 * right now and is the thing people came for; a list of dates sitting open
 * underneath it competes with that every single morning for the sake of a
 * question most people ask once a month. It opens when asked and not before,
 * and it fetches nothing until then.
 *
 * WHAT IT IS NOT: the brief above is per-user and moves with you - "since YOU
 * last looked". These are per-organisation and fixed: the same Tuesday for
 * everybody. Two different questions, deliberately two different objects.
 *
 * That distinction was spelled out on screen and Damon removed it: the panel is
 * called "Earlier briefs" and lists dates, which says enough. It stays in this
 * comment because the next person editing the file needs it; the person reading
 * the dashboard does not.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

interface Day {
  date: string;
  quiet: boolean;
  positives: number;
  negatives: number;
  neutrals: number;
}

interface Change {
  name: string; client: string; from: string; to: string; reason: string;
}

interface Archived {
  date: string;
  narrative: string;
  quiet: boolean;
  positives: Change[];
  negatives: Change[];
  neutrals: Change[];
}

/** 2026-08-11 -> "Tuesday, 11 August". Parsed as a plain date, never as UTC. */
function pretty(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return date;
  // Constructed in LOCAL time on purpose: new Date('2026-08-11') is parsed as
  // UTC midnight and prints as the 10th for anybody west of Greenwich, which
  // would put the wrong day name on every row.
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
}

function Row({ c }: { c: Change }) {
  return (
    <li>
      <span className="brief-machine">{c.name}</span>
      {c.client && <span className="dim"> ({c.client})</span>}
      <span className="dim"> {c.from} &rarr; {c.to}</span>
      <div className="brief-reason">{c.reason}</div>
    </li>
  );
}

export function BriefArchive({ orgId }: { orgId: string | null }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState<Day[] | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [brief, setBrief] = useState<Archived | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Nothing is fetched until somebody opens it. See the note at the top.
  useEffect(() => {
    if (!open || !orgId || days) return;
    setBusy(true);
    void (async () => {
      try {
        const r = await httpsCallable(functions, 'listArchivedBriefs')({ orgId, limit: 60 });
        setDays((r.data as { days: Day[] }).days ?? []);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally { setBusy(false); }
    })();
  }, [open, orgId, days]);

  async function show(date: string) {
    if (!orgId) return;
    setPicked(date); setBrief(null); setErr(null); setBusy(true);
    try {
      const r = await httpsCallable(functions, 'getArchivedBrief')({ orgId, date });
      const d = r.data as { found: boolean; brief: Archived | null };
      setBrief(d.found ? d.brief : null);
      if (!d.found) {
        // Said explicitly. An empty panel would read as "a quiet day", which is
        // a completely different fact from "we were not recording yet".
        setErr('No brief was recorded for that day.');
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  if (!orgId) return null;

  return (
    <section className="brief-archive">
      <button className="btn-link" onClick={() => setOpen((o) => !o)}>
        {open ? 'Hide earlier briefs' : 'Earlier briefs'}
      </button>

      {open && (
        <div className="brief-archive-body">

          {err && <div className="banner banner-error">{err}</div>}
          {busy && !days && <p className="brief-reason dim">Loading.</p>}

          {days?.length === 0 && (
            <p className="brief-reason dim">
              Nothing archived yet. The first record is written the morning after this was switched
              on &mdash; days before that were never captured.
            </p>
          )}

          {days && days.length > 0 && (
            <ul className="brief-archive-days">
              {days.map((d) => (
                <li key={d.date}>
                  <button
                    className={'btn-link' + (picked === d.date ? ' brief-archive-on' : '')}
                    onClick={() => void show(d.date)}
                  >
                    {pretty(d.date)}
                  </button>
                  {d.quiet ? (
                    <span className="dim"> nothing changed</span>
                  ) : (
                    <span className="dim">
                      {d.negatives > 0 && <span className="brief-count bad"> {d.negatives} need attention</span>}
                      {d.positives > 0 && <span className="brief-count good"> {d.positives} recovered</span>}
                      {d.neutrals > 0 && <span className="brief-count"> {d.neutrals} other</span>}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {brief && (
            <div className="brief-archive-detail">
              <h3 className="brief-group-title">{pretty(brief.date)}</h3>
              {brief.quiet && <p className="brief-reason dim">Nothing changed on this day.</p>}
              {brief.narrative && <p className="brief-narrative">{brief.narrative}</p>}

              {brief.negatives.length > 0 && (
                <div className="brief-group">
                  <div className="brief-group-title">Needed attention</div>
                  <ul className="brief-list">{brief.negatives.map((c, i) => <Row key={i} c={c} />)}</ul>
                </div>
              )}
              {brief.positives.length > 0 && (
                <div className="brief-group">
                  <div className="brief-group-title good">Recovered</div>
                  <ul className="brief-list">{brief.positives.map((c, i) => <Row key={i} c={c} />)}</ul>
                </div>
              )}
              {brief.neutrals.length > 0 && (
                <div className="brief-group">
                  <div className="brief-group-title">Everything else</div>
                  <ul className="brief-list">{brief.neutrals.map((c, i) => <Row key={i} c={c} />)}</ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
